"use client"

import useScrollLock from "@lib/hooks/use-scroll-lock"
import { SITE_NAME } from "@lib/util/env"
import { BarsThree, XMark } from "@medusajs/icons"
import { Link } from "@i18n/navigation"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"

const sideMenuItems = [
  { key: "home" as const, href: "/" },
  { key: "store" as const, href: "/store" },
  { key: "account" as const, href: "/account" },
  { key: "cart" as const, href: "/cart" },
]

const SideMenu = () => {
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useScrollLock(open)

  useEffect(() => {
    if (!open) return

    closeButtonRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
        requestAnimationFrame(() => triggerRef.current?.focus())
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open])

  const closeMenu = () => {
    setOpen(false)
    requestAnimationFrame(() => triggerRef.current?.focus())
  }

  return (
    <div className="flex h-full items-center">
      <button
        ref={triggerRef}
        type="button"
        aria-label={t("SideMenu.menuButton")}
        aria-expanded={open}
        aria-controls="site-side-menu"
        data-testid="nav-menu-button"
        onClick={() => setOpen(true)}
        className="relative flex h-full items-center gap-1 text-ui-fg-subtle transition-colors duration-200 hover:text-ui-fg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B88736]/40"
      >
        <BarsThree />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[70] bg-black/[0.08] backdrop-blur-[2px]"
            aria-hidden="true"
            onMouseDown={closeMenu}
            data-testid="side-menu-backdrop"
          />

          <aside
            id="site-side-menu"
            role="dialog"
            aria-modal="true"
            aria-label={t("SideMenu.menuButton")}
            data-testid="nav-menu-popup"
            className="fixed inset-y-2 start-2 z-[71] flex w-[calc(100%-1rem)] max-w-[430px] flex-col justify-between rounded-[20px] border border-[#D8C7A6]/60 bg-[#F7F3ED]/[0.98] p-6 text-[#171512] shadow-[0_30px_80px_rgba(46,34,18,0.14)] sm:w-[min(430px,38vw)]"
          >
            <div className="flex justify-end">
              <button
                ref={closeButtonRef}
                type="button"
                data-testid="close-menu-button"
                aria-label={t("Common.close")}
                onClick={closeMenu}
                className="rounded-full p-2 transition-colors hover:bg-black/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B88736]/40"
              >
                <XMark />
              </button>
            </div>

            <nav aria-label={t("SideMenu.menuButton")}>
              <ul className="flex flex-col items-start gap-5">
                {sideMenuItems.map(({ key, href }) => (
                  <li key={key}>
                    <Link
                      href={href}
                      className="text-[clamp(2rem,5vw,3.5rem)] font-normal leading-[1.05] tracking-[-0.04em] transition-opacity hover:opacity-45"
                      onClick={closeMenu}
                      data-testid={`${key}-link`}
                    >
                      {t(`SideMenu.items.${key}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <p className="text-[11px] uppercase tracking-[0.12em] text-[#756D62]">
              {t("SideMenu.copyright", {
                year: new Date().getFullYear(),
                siteName: SITE_NAME,
              })}
            </p>
          </aside>
        </>
      )}
    </div>
  )
}

export default SideMenu
